import { defineComponent as _defineComponent } from 'vue'
import Vue, { computed, ref } from 'vue';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { mrcObservableOptions } from '@/helpers/composables/useBatchImpressionTracking';
import { ThumborOptions } from '@/filters/thumbor';
import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import DisclaimerButton from '@/features/buybox/components/DisclaimerButton.vue';
import { useI18n } from '@/helpers/composables/useI18n';
import { QTVSimilarTitle, useQTV } from '@/features/buybox/composables/useQTV';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { ClickoutUrl, getClickoutUrlContextsArray, trackClickout } from '@/helpers/clickout-helper';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import { qtvProviderType } from '@/features/buybox/helpers/quality-tv-data';


export default /*#__PURE__*/_defineComponent({
  __name: 'QualityTvRow',
  props: {
    providerShortName: null,
    shortName: null,
    title: null,
    isFreeBuyBox: { type: Boolean },
    additionalContexts: null,
    trackingContexts: null,
    seasonNumber: null,
    episodeNumber: null
  },
  setup(__props: any) {

const props = __props as {
	providerShortName?: string;
	shortName: string;
	title: TitleDetail;
	isFreeBuyBox: boolean;
	additionalContexts: SnowplowContext[];
	trackingContexts: SnowplowContext[];
	seasonNumber?: number | null;
	episodeNumber?: number | null;
};



const { tc } = useI18n();

const titleListContainer = ref<HTMLElement | null>(null);
const titlesShownCount = computed(() => {
	const containerWidth = titleListContainer.value?.clientWidth || 0;
	const posterWidth = 65;
	const gap = 22;
	// in order to count the number visible in the viewport we divide the width of the container by the width of the poster + gap
	return Math.round(containerWidth / (posterWidth + gap));
});

const qtvAdditionalContexts = computed(() => props.additionalContexts.concat(props.trackingContexts));

const { qtvTitles, onVisibilityChanged, clickoutUrls, ctaClickoutUrl, onCTAVisibilityChanged } = useQTV({
	shortName: () => props.shortName,
	sourceTitle: () => props.title,
	titlesShownCount: titlesShownCount,
	additionalContexts: qtvAdditionalContexts,
	seasonNumber: () => props.seasonNumber,
	episodeNumber: () => props.episodeNumber,
});

const retailPrice = computed(() => {
	// Getting the price of Apple TV+ package from the first appleTitle
	// prettier-ignore
	return qtvTitles.value?.[0]
				?.offers?.find(offer => offer.package.shortName === 'atp')
				?.package?.planOffers?.[0]
				?.retailPrice;
});

const qualityTVType = computed(() => qtvProviderType[props.shortName]);

const isFreeTrialQTV = computed(() => qualityTVType.value === 'FREE');

const isTVODTrialQTV = computed(() => {
	return qualityTVType.value === 'TVOD';
});

const ctaText = computed(() => {
	if (qualityTVType.value === 'JWTV') return tc('WEBAPP_STREAM_FOR_FREE');
	// currently handling TVOD in template
	return qualityTVType.value === 'SVOD' ? tc('WEBAPP_STREAM_SIMILAR_TITLES') : tc('WEBAPP_STREAM_WITH_FREE_TRIAL');
});

const isModal = computed(() => props.shortName === 'jwt');

// A map that caches visibility callbacks for titles by their unique ID.
// This ensures each title has a stable, reusable callback reference.
// This is important because impression tracking causes a re-render of the component, and a loop of new callbacks would be created otherwise.
const observerCallbacks = new Map<string, (isVisible: boolean) => void>();

function imageUrl(titlePosterUrl: string | null | undefined) {
	return Vue.filter('Thumbor')({
		url: titlePosterUrl,
		imageType: 'poster',
	} as ThumborOptions);
}

function imbdScore(title: { content: { scoring: { imdbScore?: number | null } } }) {
	return title.content.scoring?.imdbScore?.toFixed(1);
}

function handleQTVClick(qtvTitle: QTVSimilarTitle, url: ClickoutUrl) {
	if (!isModal.value) return;
	if (url) trackClickout(url);
	ModalHelper.openModal(
		() => import('@/ui-components/video-player-modals/JwTVModal.vue'),
		{ offer: qtvTitle.offers[0], title: qtvTitle, additionalContexts: getClickoutUrlContextsArray(url) },
		{ cssClass: 'trailer-modal' },
		'',
		true,
		[]
	);
}

/**
 * Returns a stable visibility callback for a given title.
 * If one doesn't already exist for the title ID, it creates and stores it.
 * This memoization ensures consistent identity (important to avoid re-rendering issues)
 */

function getVisibilityCallback(title: QTVSimilarTitle, index: number) {
	if (!observerCallbacks.has(title.id)) {
		observerCallbacks.set(title.id, isVisible => onVisibilityChanged(title, index, isVisible));
	}
	return observerCallbacks.get(title.id)!;
}

function handleObservation(title: QTVSimilarTitle, index: number) {
	const stableCallback = getVisibilityCallback(title, index);
	return {
		...mrcObservableOptions,
		callback: stableCallback,
	};
}

function getCTAVisiblityCalback() {
	if (!observerCallbacks.has('cta')) {
		observerCallbacks.set('cta', isVisible => onCTAVisibilityChanged(isVisible));
	}
	return observerCallbacks.get('cta')!;
}

function handleCTAObservation() {
	const stableCallback = getCTAVisiblityCalback();
	return {
		...mrcObservableOptions,
		callback: stableCallback,
	};
}

return { __sfc: true,props, tc, titleListContainer, titlesShownCount, qtvAdditionalContexts, qtvTitles, onVisibilityChanged, clickoutUrls, ctaClickoutUrl, onCTAVisibilityChanged, retailPrice, qualityTVType, isFreeTrialQTV, isTVODTrialQTV, ctaText, isModal, observerCallbacks, imageUrl, imbdScore, handleQTVClick, getVisibilityCallback, handleObservation, getCTAVisiblityCalback, handleCTAObservation, ProviderIcon, DisclaimerButton, ClickoutAnchor }
}

})